import {
    cnTextField,
    type TextFieldPropOnChange,
    type TextFieldPropStatus,
    type TextFieldPropValue,
} from 'components/FieldNumberFormat';
import { Button, cnMixSpace, IconCheck, IconClose, Tooltip, usePropsHandler } from 'uikit';

import cn from 'classnames';
import { type ChangeEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import Styles from './catalogEditValueInput.module.css';

interface ICatalogEditValueInput {
    value: TextFieldPropValue;
    onChange: TextFieldPropOnChange;
    onCancel: () => void;
    onSubmit: () => void;
    status: TextFieldPropStatus | undefined;
    tooltipMsg: string;
    inputPlaceholder: string;
    applyDisabled?: boolean;
    isTooltipVisible?: boolean;
}

export const CatalogEditValueInput = ({
    onCancel,
    onSubmit,
    tooltipMsg,
    inputPlaceholder,
    applyDisabled,
    isTooltipVisible,
    ...props
}: ICatalogEditValueInput) => {
    const ref = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.focus();
    }, []);

    const { value, onChange, status, type, size, width, placeholder, view, form, disabled, autofocus } =
        usePropsHandler(
            'TextField',
            {
                type: 'text',
                size: 's',
                width: 'full',
                placeholder: inputPlaceholder,
                view: 'default',
                form: 'default',
                disabled: false,
                autofocus: true,
                ...props,
            },
            ref,
        );

    const [focus, setFocus] = useState<boolean>(autofocus);

    const handleBlur = useCallback(() => {
        setFocus(false);
    }, []);

    const handleFocus = useCallback(() => {
        setFocus(true);
    }, []);

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            onChange({ e: event, value: event.target.value || null });
        },
        [onChange],
    );

    return (
        <>
            <div className={cnTextField({ size, view, width })} ref={ref}>
                <div className={cnTextField('Body')}>
                    <div
                        className={cnTextField('InputContainer', {
                            view,
                            form,
                            status,
                            disabled,
                            type,
                            focus,
                            withValue: !!value,
                        })}
                        ref={inputContainerRef}
                    >
                        <input
                            className={cnTextField('Input')}
                            value={value ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            disabled={disabled}
                            placeholder={placeholder}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={autofocus}
                        />
                        <Button
                            className={cn(cnTextField('ClearButton'), Styles.button)}
                            onClick={onSubmit}
                            onlyIcon
                            iconRight={IconCheck}
                            size="xs"
                            view="clear"
                            disabled={!value || !!applyDisabled}
                        />
                        <Button
                            className={cn(cnTextField('ClearButton'), cnMixSpace({ mL: '2xs' }), Styles.button)}
                            onClick={onCancel}
                            onlyIcon
                            iconRight={IconClose}
                            size="xs"
                            view="clear"
                        />
                    </div>
                </div>
            </div>
            {status && isTooltipVisible !== false && (
                <Tooltip size="l" direction="rightCenter" status="alert" anchorRef={inputContainerRef}>
                    {tooltipMsg}
                </Tooltip>
            )}
        </>
    );
};

CatalogEditValueInput.displayName = 'CatalogEditValueInput';
